import { Box } from "@mui/material";
import { Basket, QuickRetail } from "@pmag/icons";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { dispatchSPFAddToCartEvent, Events } from "@src/utils/events";
import { isSPFAddToCartProcessedEvent } from "@src/utils/typeGuard.utils";
import { useSnackbar } from "notistack";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { Root, StyledNotification } from "./AddToCartButton.styles";

interface AddToCartButtonProps {
    amount: number;
    articleNumber: string;
    brand: string;
    clientArticleNumber?: string;
}

let timeoutId: NodeJS.Timeout;

export const AddToCartButton: React.FunctionComponent<AddToCartButtonProps> = ({ articleNumber, brand, amount, clientArticleNumber }) => {
    const attributeConfig = useAttributeConfig();
    const snackbar = useSnackbar();
    const [loading, setLoading] = React.useState(false);
    const [showAddedString, setShowAddedString] = React.useState(false);
    const [added, setAdded] = React.useState(false);

    const checkForTimeout = () => {
        setLoading(false);
        if (!added) {
            // eslint-disable-next-line @pmag/react/enqueue-snackbar-variant-error
            snackbar?.enqueueSnackbar("", {
                variant: "error",
                persist: true,
                content: (key: string) => (
                    <Box>
                        <StyledNotification severity="error" onClose={() => snackbar.closeSnackbar(key)}>
                            <FormattedMessage
                                id="sparePartsFinder.subgroupPage.priceCell.addToCart.timeout"
                                defaultMessage="Error adding article to cart: {articleNumber} request timeout"
                                values={{ articleNumber: articleNumber }}
                            />
                        </StyledNotification>
                    </Box>
                ),
            });
        }
    };

    const addToCart = () => {
        setAdded(false);
        if (!loading && !showAddedString) {
            setLoading(true);
            dispatchSPFAddToCartEvent(articleNumber, brand, amount, clientArticleNumber);
            timeoutId = setTimeout(checkForTimeout, 3000);
        }
    };

    React.useEffect(() => {
        if (attributeConfig.hasAddToCart) {
            const handleAddToCartProcessed = (event: CustomEvent) => {
                if (isSPFAddToCartProcessedEvent(event)) {
                    if (event.detail.successfulArticleNumbers.includes(articleNumber)) {
                        setLoading(false);
                        if (!added) {
                            setAdded(true);
                            setShowAddedString(true);
                            clearTimeout(timeoutId);
                            setTimeout(() => setShowAddedString(false), 800);
                        }
                    }
                    if (event.detail.errorArticleNumbers && event.detail.errorArticleNumbers.includes(articleNumber)) {
                        setLoading(false);
                        // eslint-disable-next-line @pmag/react/enqueue-snackbar-variant-error
                        snackbar?.enqueueSnackbar("", {
                            variant: "error",
                            persist: true,
                            content: (key: string) => (
                                <StyledNotification severity="error" onClose={() => snackbar.closeSnackbar(key)}>
                                    <FormattedMessage
                                        id="sparePartsFinder.subgroupPage.priceCell.addToCart.error"
                                        defaultMessage="Error adding article to cart: {articleNumber}"
                                        values={{ articleNumber: articleNumber }}
                                    />
                                </StyledNotification>
                            ),
                        });
                    }
                }
            };

            document.addEventListener(Events.SPFAddToCartProcessed, handleAddToCartProcessed);

            return () => document.removeEventListener(Events.SPFAddToCartProcessed, handleAddToCartProcessed);
        }
    }, [added, articleNumber, attributeConfig.hasAddToCart, snackbar]);

    return (
        <Root size="small" icon={showAddedString ? undefined : loading ? <QuickRetail size="small" /> : <Basket size="small" />} onClick={addToCart}>
            {showAddedString ? (
                <FormattedMessage id="sparePartsFinder.subgroupPage.button.addedToCart" defaultMessage="Added" />
            ) : (
                !loading && <FormattedMessage id="sparePartsFinder.subgroupPage.button.addToCart" defaultMessage="Add to cart" />
            )}
        </Root>
    );
};
